import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { getChartColors, getChartStructuralStyles } from "theme";
import { HtmlTooltip } from "components/HeatMap";
import { appendTooltipGlossary } from "components/reports/shared/tooltipGlossary";

/**
 * ReportBarChart - Wrapper for MUI BarChart with consistent styling
 *
 * @param {string} title - Chart title
 * @param {Array} data - Array of data objects
 * @param {string} xAxisKey - Key for x-axis data
 * @param {string} tooltipKey - Optional key for tooltip display (defaults to xAxisKey)
 * @param {Array} series - Array of series configs: [{ dataKey: 'revenue', label: 'Revenue', color: '#...' }]
 * @param {number} height - Chart height in pixels
 * @param {boolean} showInPaper - Wrap in Paper component
 * @param {string} xAxisLabel - Label for x-axis
 * @param {string} yAxisLabel - Label for y-axis
 * @param {Function} valueFormatter - Function to format values
 * @param {Function} onBarClick - Optional click handler for bar elements
 * @param {string} tooltipTitle - Optional tooltip title for info icon
 * @param {string} tooltipContent - Optional tooltip content/explanation
 * @param {boolean} showLegend - Show legend (default: true)
 */
const ReportBarChart = ({
  title,
  data = [],
  xAxisKey,
  tooltipKey,
  series = [],
  height = 400,
  showInPaper = true,
  xAxisLabel,
  yAxisLabel,
  valueFormatter = (value) => value?.toLocaleString(),
  onBarClick,
  tooltipTitle,
  tooltipContent,
  showLegend = true,
  yAxisScaleType = "sqrt",
  xAxisPosition = "bottom",
  chartMargin,
  legendSlotProps,
  yAxisConfig,
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const resolvedTooltipContent = appendTooltipGlossary(tooltipContent);
  const legendVerticalPosition = legendSlotProps?.position?.vertical;
  const resolvedMargin = chartMargin || { top: 20, right: 20, bottom: 50, left: 80 };
  const chartResolvedMargin =
    showLegend && legendVerticalPosition === "bottom"
      ? {
          ...resolvedMargin,
          bottom: Math.max(resolvedMargin.bottom || 0, 110),
        }
      : resolvedMargin;
  const resolvedLegendSlotProps =
    showLegend && legendVerticalPosition === "bottom"
      ? {
          ...legendSlotProps,
          padding: Math.max(legendSlotProps?.padding || 0, 12),
        }
      : legendSlotProps;
  
  const chartContent = (
    <Box>
      {title && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          {resolvedTooltipContent && (
            <HtmlTooltip
              title={
                <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto" }}>
                  {tooltipTitle && (
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {tooltipTitle}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {resolvedTooltipContent}
                  </Typography>
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.primary.main,
                  cursor: "help",
                  opacity: 0.7,
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              />
            </HtmlTooltip>
          )}
        </Box>
      )}
      {data.length === 0 ? (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.background.alt,
            borderRadius: "8px",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      ) : (
        <BarChart
          borderRadius={10}
          dataset={data}
          xAxis={[
            {
              scaleType: "band",
              dataKey: xAxisKey,
              label: xAxisLabel,
              position: xAxisPosition,
            },
          ]}
          yAxis={[
            {
              scaleType: yAxisScaleType,
              label: yAxisLabel,
              ...yAxisConfig,
            },
          ]}
          series={series.map((s, index) => ({
            dataKey: s.dataKey,
            label: s.label,
            color:
              s.color || chartColors.series[index % chartColors.series.length],
            valueFormatter: s.valueFormatter || valueFormatter,
          }))}
          height={height}
          margin={chartResolvedMargin}
          onItemClick={
            onBarClick
              ? (event, d) => {
                  const item = data[d.dataIndex];
                  if (item) onBarClick(item);
                }
              : undefined
          }
          tooltip={{
            trigger: "axis",
          }}
          axisHighlight={{
            x: "band",
            y: "none",
          }}
          slotProps={{
            legend: { hidden: !showLegend, ...resolvedLegendSlotProps },
            ...(tooltipKey && {
              axisContent: (props) => {
                if (!props || props.dataIndex === undefined) return null;
                const item = data[props.dataIndex];
                if (!item) return null;

                return (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "background.paper",
                      boxShadow: 2,
                      borderRadius: 1,
                      maxWidth: 400,
                      maxHeight: 400,
                      overflowY: "auto",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{ mb: 0.5 }}
                    >
                      {item[tooltipKey] || item[xAxisKey]}
                    </Typography>
                    {series.map((s) => (
                      <Typography
                        key={s.dataKey}
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: item.expenses && item.expenses.length > 0 ? 1 : 0,
                        }}
                      >
                        {s.label}:{" "}
                        {(s.valueFormatter || valueFormatter)(item[s.dataKey])}
                      </Typography>
                    ))}
                    {item.expenses && item.expenses.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="600"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Individual Expenses:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {item.expenses.map((expense, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 2,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ flex: 1, fontSize: "0.7rem" }}
                              >
                                {expense.description || "No description"}
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight="500"
                                sx={{
                                  fontSize: "0.7rem",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {valueFormatter(expense.amount)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              },
            }),
          }}
          sx={{
            ...getChartStructuralStyles(theme),
            "& .MuiChartsLegend-mark": {
              rx: 2,
              ry: 2,
            },
            "& .MuiChartsLegend-series rect": {
              rx: 2,
              ry: 2,
            },
            ...(onBarClick && {
              "& .MuiBarElement-root": {
                cursor: "pointer",
              },
            }),
          }}
        />
      )}
    </Box>
  );

  if (showInPaper) {
    return (
      <Paper
        sx={{
          p: 3,
          bgcolor: theme.palette.background.alt,
          border: `1px solid ${theme.palette.primary.border2}`,
          borderRadius: "12px",
        }}
      >
        {chartContent}
      </Paper>
    );
  }

  return chartContent;
};

export default ReportBarChart;
